import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import { useBgReport, useBgJob } from './BackgroundJobs';

// Verifica 456 singola (free-text) via /ricerca-online. Estetica fumé dei dialog agenti.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const FASI = { avvio: 'Avvio', in_coda: 'In coda', chiamata: 'Chiamata', registrazione: 'Registrazione', trascrizione: 'Trascrizione', analisi: 'Analisi' };
const esitoLabel = (r) => ({ TIM: 'TIM', 'NON TIM': 'NON TIM', occupato: 'Occupato', no_risposta: 'Nessuna risposta', numero_inesistente: 'Numero inesistente', errore: 'Errore' }[r] || r || '—');
const esitoColor = (r) => r === 'NON TIM' ? '#22c55e' : r === 'TIM' ? '#e11d48' : '#f59e0b';

const BTN_REG = { padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };

const mmss = (s) => {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s - m * 60)).padStart(2, '0')}`;
};

// Ascolto della registrazione dentro il dialog: parte da sola (arriva dopo un click) e
// vive solo finche' il dialog e' aperto. Niente file, niente cronologia: solo memoria.
function RegPlayer({ src }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { const p = a.play(); if (p && p.catch) p.catch(() => {}); } else a.pause();
  };
  const seek = (e) => {
    const a = ref.current;
    if (!a || !dur) return;
    const box = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)) * dur;
  };
  const pct = dur ? (pos / dur) * 100 : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)' }}>
      <button onClick={toggle} title={on ? 'Pausa' : 'Ascolta'} aria-label={on ? 'Pausa' : 'Ascolta'}
        style={{ flex: '0 0 30px', width: 30, height: 30, borderRadius: '50%', border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'linear-gradient(180deg,#34d399,#059669)' }}>
        {on
          ? <svg width="11" height="12" viewBox="0 0 11 12" fill="currentColor"><rect x="0" y="0" width="3.5" height="12" rx="1" /><rect x="7.5" y="0" width="3.5" height="12" rx="1" /></svg>
          : <svg width="11" height="12" viewBox="0 0 11 12" fill="currentColor"><path d="M1 .8 10 6l-9 5.2z" /></svg>}
      </button>
      <div onClick={seek} style={{ flex: 1, minWidth: 40, height: 6, borderRadius: 3, background: 'rgba(255,255,255,.14)', cursor: dur ? 'pointer' : 'default' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#059669,#34d399)' }} />
      </div>
      <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)', fontVariantNumeric: 'tabular-nums' }}>{mmss(pos)} / {mmss(dur)}</span>
      <audio ref={ref} src={src} autoPlay preload="auto"
        onPlay={() => setOn(true)} onPause={() => setOn(false)}
        onEnded={() => { setOn(false); setPos(0); }}
        onTimeUpdate={(e) => setPos(e.target.currentTime)}
        onLoadedMetadata={(e) => setDur(e.target.duration)} />
    </div>
  );
}

export default function N456Dialog({ open, onClose }) {
  const [numero, setNumero] = useState('');
  const [busy, setBusy] = useState(false);
  const [fase, setFase] = useState('');
  const [esito, setEsito] = useState(null);
  const [regStato, setRegStato] = useState(null);   // null | 'carico' | 'pronta' | 'non_disp'
  const [player, setPlayer] = useState(null);        // url del blob, quando si sta ascoltando
  const [jobId, setJobId] = useState(null);
  const poll = useRef(null);
  const regUrl = useRef(null);

  // Il blob della registrazione va rilasciato: e' l'unica copia rimasta ed e' solo in memoria.
  const liberaReg = () => {
    if (regUrl.current) { window.URL.revokeObjectURL(regUrl.current); regUrl.current = null; }
    setPlayer(null); setRegStato(null);
  };

  useEffect(() => () => { clearInterval(poll.current); if (regUrl.current) window.URL.revokeObjectURL(regUrl.current); }, []);

  const clear = () => { clearInterval(poll.current); setNumero(''); setBusy(false); setFase(''); setEsito(null); liberaReg(); setJobId(null); };

  const errore = !!esito && (esito.errore || esito.risultato === 'errore');
  const summary = esito ? esitoLabel(esito.risultato) : null;
  useBgReport('456', { label: 'Verifica 456', color: '#10b981', iconKey: '456' },
    { open, running: busy, done: !busy && !!esito && !errore, error: errore, summary, jobId, active: busy || !!esito });

  // reset locale quando la notifica viene chiusa (×)
  const entry = useBgJob('456');
  const had = useRef(false);
  useEffect(() => { if (entry) had.current = true; else if (had.current) { had.current = false; clear(); } }, [entry]); // eslint-disable-line
  // riaprendo la notifica: ripesca l'esito dal server col jobId se non l'ho in locale
  useEffect(() => {
    if (open && !busy && !esito && entry && entry.jobId) {
      axios.get(`${API}/ricerca-online/${entry.jobId}`).then((r) => { if (r.data.stato === 'completato' || r.data.stato === 'errore') setEsito(r.data); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const avvia = async () => {
    const n = numero.trim();
    if (!n) return;
    clearInterval(poll.current);
    setBusy(true); setEsito(null); liberaReg(); setFase('avvio');
    try {
      const r = await axios.post(`${API}/ricerca-online`, { numero: n });
      const id = r.data.id; setJobId(id);
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/ricerca-online/${id}`);
          setFase(s.data.fase || 'avvio');
          if (s.data.stato === 'completato' || s.data.stato === 'errore') {
            clearInterval(poll.current); setEsito(s.data); setBusy(false);
          }
        } catch { /* ritenta */ }
      }, 1000);
    } catch (e) {
      setEsito({ risultato: 'errore', errore: e.response?.data?.detail || 'Errore verifica' }); setBusy(false);
    }
  };

  // Il server consegna l'MP3 UNA volta sola: quel GET lo cancella dal disco. Lo prendiamo
  // quindi una volta e lo teniamo in memoria, cosi' lo stesso file serve sia per ascoltarlo
  // sia per scaricarlo. Alla fine sparisce con il dialog: non viene salvato da nessuna parte.
  const prendiReg = async () => {
    if (regUrl.current) return regUrl.current;
    if (!esito?.id) return null;
    setRegStato('carico');
    try {
      const r = await axios.get(`${API}/ricerca-online/${esito.id}/registrazione`, { responseType: 'blob' });
      regUrl.current = window.URL.createObjectURL(r.data);
      setRegStato('pronta');
      return regUrl.current;
    } catch { setRegStato('non_disp'); return null; }
  };

  const ascoltaReg = async () => { const url = await prendiReg(); if (url) setPlayer(url); };

  const scaricaReg = async () => {
    const url = await prendiReg();
    if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `verifica_${esito.numero || numero}.mp3`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Verifica 456</span>
            <button className="bulk-x" onClick={onClose}><IconClose /></button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Numero fisso da verificare"
              onKeyDown={(e) => { if (e.key === 'Enter') avvia(); }} disabled={busy}
              style={{ flex: 1, minWidth: 0, padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 13 }} />
            <button onClick={avvia} disabled={busy || !numero.trim()}
              style={{ padding: '9px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy || !numero.trim() ? 'default' : 'pointer', opacity: busy || !numero.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#34d399,#059669)' }}>
              {busy ? '…' : 'Verifica'}
            </button>
          </div>

          {busy ? (
            <div style={{ padding: '4px 0 6px' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>{FASI[fase] || 'In corso'}…</div>
              <div className="indaga-track"><div className="indaga-fill" style={{ width: '100%', background: 'linear-gradient(90deg,#059669,#34d399,#059669)' }} /></div>
            </div>
          ) : esito ? (
            esito.errore ? <div style={{ color: '#fca5a5', fontSize: 13 }}>{esito.errore}</div> : (
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: '#fff', background: esitoColor(esito.risultato), padding: '6px 14px', borderRadius: 10, marginBottom: 10 }}>
                  {esitoLabel(esito.risultato)}
                </div>
                <div style={{ display: 'flex', gap: 10, fontSize: 13, padding: '5px 0', color: 'rgba(255,255,255,.85)' }}>
                  <span style={{ color: 'rgba(255,255,255,.5)', flex: '0 0 90px' }}>Numero</span>
                  <span>{esito.numero || numero}</span>
                </div>
                {esito.trascrizione && (
                  <div style={{ display: 'flex', gap: 10, fontSize: 13, padding: '5px 0', color: 'rgba(255,255,255,.85)' }}>
                    <span style={{ color: 'rgba(255,255,255,.5)', flex: '0 0 90px' }}>Trascrizione</span>
                    <span style={{ flex: 1, minWidth: 0, fontStyle: 'italic' }}>“{esito.trascrizione}”</span>
                  </div>
                )}
                {esito.id && esito.audio_file && regStato !== 'non_disp' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {!player && (
                      <button onClick={ascoltaReg} disabled={regStato === 'carico'} style={BTN_REG}>
                        {regStato === 'carico' ? '…' : '▶ Ascolta'}
                      </button>
                    )}
                    <button onClick={scaricaReg} disabled={regStato === 'carico'} style={BTN_REG}>⭳ Scarica</button>
                  </div>
                )}
                {player && <RegPlayer src={player} />}
                {regStato === 'non_disp' && <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,.5)' }}>Registrazione non disponibile.</div>}
              </div>
            )
          ) : entry && entry.status !== 'running' ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '8px 0' }}>Ultimo esito: <b style={{ color: '#fff' }}>{entry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova verifica sopra)</span></div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '8px 0' }}>
              Inserisci un numero di rete fissa e premi Verifica: chiamata 456, registrazione, trascrizione e esito TIM / NON TIM.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
